import { z } from 'zod';
import type { AssetType, ProcessingState, SortDirection, SortField, ViewMode } from '@ael/shared-components-library';
import { PortalApiClient } from '../rest-api/portalApiClient';
import { SubscriptionTier } from '../types/Organization';

type ProjectsListingFilter = Omit<NonNullable<Parameters<PortalApiClient['listProjects']>[0]>, 'cursor'>;
type OrderByOption = NonNullable<ProjectsListingFilter['orderBy']>;

export const orderByOptions = [
  'Newest',
  'Oldest',
  'Alphabetical',
  'ReverseAlphabetical',
  'RecentlyModified',
  'LeastRecentlyModified',
] as const satisfies readonly OrderByOption[];

export const projectsSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  // kept as the raw comma-separated string ('a,b') so existing bookmarked URLs keep working;
  // splitting/joining happens in useProjectsFilters
  tags: z.string().optional().catch(undefined),
  sort: z.enum(orderByOptions).optional().catch(undefined),
  // tolerates the legacy value-less '?welcome' form, which parses as an empty string
  welcome: z
    .union([z.boolean(), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? true : value))
    .catch(undefined),
});

export type ProjectsSearch = z.infer<typeof projectsSearchSchema>;

const subscriptionTiers = ['Free', 'Pro', 'Team', 'Enterprise'] as const satisfies readonly SubscriptionTier[];

export const subscriptionPendingSearchSchema = z.object({
  sub: z.enum(subscriptionTiers).optional().catch(undefined),
});

const mediaViewModes = ['list', 'grid'] as const satisfies readonly ViewMode[];
const mediaAssetTypes = [
  'image',
  'audio',
  'video',
  'document',
  'subtitle',
  'web',
] as const satisfies readonly AssetType[];
const mediaProcessingStates = ['ready', 'processing', 'failed'] as const satisfies readonly ProcessingState[];
const mediaSorts = [
  'uploadDate-desc',
  'uploadDate-asc',
  'name-asc',
  'name-desc',
  'size-desc',
  'size-asc',
] as const satisfies readonly `${SortField}-${SortDirection}`[];

/*
 * A comma-separated list ('image,video'), read past anything it does not recognise. Stays a string:
 * the router runs `validateSearch` on the search it navigates to and writes the *output* into the
 * URL, so an array here would land there as `["image"]` and fail the next read.
 */
const commaSeparated = (values: readonly string[]) =>
  z
    .string()
    .optional()
    .transform(
      (raw) =>
        raw
          ?.split(',')
          .filter((value) => values.includes(value))
          .join(',') || undefined
    )
    .catch(undefined);

// Every field: absent means the library's default, and anything unrecognised (a hand-edited URL)
// falls back to it.
export const mediaSearchSchema = z.object({
  view: z.enum(mediaViewModes).optional().catch(undefined),
  // coerced: the router parses a hand-typed `?q=123` into a number
  q: z.coerce.string().optional().catch(undefined),
  types: commaSeparated(mediaAssetTypes),
  states: commaSeparated(mediaProcessingStates),
  sort: z.enum(mediaSorts).optional().catch(undefined),
});

export type MediaSearch = z.infer<typeof mediaSearchSchema>;

export const projectSettingsParamsSchema = z.object({
  project_id: z.coerce.number().int().positive(),
});

export const courseExportDetailParamsSchema = z.object({
  course_version_guid: z.string(),
});

export const courseExportVersionPreviewParamsSchema = courseExportDetailParamsSchema.extend({
  version_guid: z.string(),
});
